import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import TrialBanner from '@/pages/dashboard/components/TrialBanner';
import { TRIAL_DURATION_DAYS } from '@/hooks/planAccess';

interface PlanInfo {
  name: string;
  tag: string;
  price: string;
  period: string;
  priceRaw: number;
  messagesPerMonth: string;
  messageLimit: number;
  users: string;
  whatsapps: string;
  features: string[];
  highlighted: boolean;
  planKey: string;
  cta: string;
  connectionLabel: string;
  excessBlockPrice: number;
  excessBlockSize: number;
  setupFee: number;
}

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  highlighted: boolean;
}

const plansList: PlanInfo[] = [
  {
    name: 'Trial',
    tag: 'Grátis',
    price: 'R$ 0',
    period: '/7 dias',
    priceRaw: 0,
    messagesPerMonth: '500 mensagens',
    messageLimit: 500,
    users: '1 usuário',
    whatsapps: '1 WhatsApp',
    features: [
      'Dashboard',
      'Contatos',
      'Créditos de envio',
      'Perfil',
      'Assinatura',
    ],
    highlighted: false,
    planKey: 'trial',
    cta: 'Começar Grátis',
    connectionLabel: 'Use sua própria API Meta durante o teste',
    excessBlockPrice: 0,
    excessBlockSize: 0,
    setupFee: 0,
  },
  {
    name: 'Starter',
    tag: 'Essencial',
    price: 'R$ 97',
    period: '/mês',
    priceRaw: 97,
    messagesPerMonth: '5.000 mensagens/mês',
    messageLimit: 5000,
    users: '1 usuário',
    whatsapps: '1 WhatsApp',
    features: [
      'Importação CSV de contatos',
      'Templates básicos',
      'Relatório de envio',
      'Botão WhatsApp',
      'Suporte online',
    ],
    highlighted: false,
    planKey: 'starter',
    cta: 'Assinar Starter',
    connectionLabel: 'Use sua própria API Meta ou conecte com WZAP',
    excessBlockPrice: 50,
    excessBlockSize: 10000,
    setupFee: 297,
  },
  {
    name: 'Professional',
    tag: 'Mais Popular',
    price: 'R$ 297',
    period: '/mês',
    priceRaw: 297,
    messagesPerMonth: '50.000 mensagens/mês',
    messageLimit: 50000,
    users: 'Até 5 usuários',
    whatsapps: 'Até 3 WhatsApps',
    features: [
      'Templates avançados',
      'Segmentação de clientes',
      'Agendamento de campanhas',
      'Relatórios Excel/PDF',
      'Automações',
      'API de integração',
    ],
    highlighted: true,
    planKey: 'professional',
    cta: 'Assinar Professional',
    connectionLabel: 'Use sua própria API Meta ou conecte com WZAP',
    excessBlockPrice: 60,
    excessBlockSize: 10000,
    setupFee: 497,
  },
  {
    name: 'Business',
    tag: 'Avançado',
    price: 'R$ 597',
    period: '/mês',
    priceRaw: 597,
    messagesPerMonth: '150.000 mensagens/mês',
    messageLimit: 150000,
    users: 'Até 10 usuários',
    whatsapps: 'Até 5 WhatsApps',
    features: [
      'CRM integrado',
      'Funil de vendas',
      'Chat em equipe',
      'Automações avançadas',
      'API dedicada',
      'Suporte prioritário',
    ],
    highlighted: false,
    planKey: 'business',
    cta: 'Assinar Business',
    connectionLabel: 'Use sua própria API Meta ou conecte com WZAP',
    excessBlockPrice: 70,
    excessBlockSize: 10000,
    setupFee: 697,
  },
  {
    name: 'Enterprise',
    tag: 'Premium',
    price: 'Sob Consulta',
    period: '',
    priceRaw: 0,
    messagesPerMonth: '500.000+ mensagens/mês',
    messageLimit: 500000,
    users: 'Usuários ilimitados',
    whatsapps: 'WhatsApps ilimitados',
    features: [
      'White label',
      'API própria do cliente',
      'Servidor dedicado',
      'Integrações personalizadas',
      'SLA garantido (99.9%)',
      'Suporte 24/7 dedicado',
      'Gerente de conta',
    ],
    highlighted: false,
    planKey: 'enterprise',
    cta: 'Falar com Vendas',
    connectionLabel: 'API própria dedicada, alto volume personalizado',
    excessBlockPrice: 80,
    excessBlockSize: 10000,
    setupFee: 997,
  },
];

const creditPackages: CreditPackage[] = [
  { id: 'cred-10k', name: '10 mil créditos', credits: 10000, price: 60, highlighted: false },
  { id: 'cred-50k', name: '50 mil créditos', credits: 50000, price: 250, highlighted: true },
  { id: 'cred-100k', name: '100 mil créditos', credits: 100000, price: 450, highlighted: false },
];

const excessPackages = [
  { id: 'excess-10k', name: '+10.000 mensagens', amount: 10000, price: 60, icon: 'ri-add-circle-line' },
  { id: 'excess-50k', name: '+50.000 mensagens', amount: 50000, price: 250, icon: 'ri-stack-line' },
  { id: 'excess-100k', name: '+100.000 mensagens', amount: 100000, price: 450, icon: 'ri-database-2-line' },
];

export default function SubscriptionPage() {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [connectionType, setConnectionType] = useState<'wzap' | 'own'>('wzap');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [loadingCredit, setLoadingCredit] = useState<string | null>(null);
  const [loadingExcess, setLoadingExcess] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'planos' | 'excedente' | 'creditos'>('planos');
  const [paymentGateway, setPaymentGateway] = useState<'mercadopago' | 'stripe'>('mercadopago');
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const currentPlan = profile?.plan || 'starter';
  const currentConnection = profile?.connection_type || 'wzap';
  const currentCredits = profile?.credits || 0;
  const monthlyLimit = profile?.monthly_message_limit || 5000;
  const sentThisMonth = profile?.messages_sent_this_month || 0;
  const usagePercent = monthlyLimit > 0 ? Math.min(100, Math.round((sentThisMonth / monthlyLimit) * 100)) : 0;

  // Trial expiration calculation
  const trialDaysRemaining = profile?.created_at && currentPlan === 'trial'
    ? Math.max(0, Math.ceil((new Date(profile.created_at).getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000 - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const trialEndDate = profile?.created_at && currentPlan === 'trial'
    ? new Date(new Date(profile.created_at).getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000)
    : null;

  useEffect(() => {
    setConnectionType(currentConnection as 'wzap' | 'own');
  }, [currentConnection]);

  useEffect(() => {
    const checkStripe = async () => {
      if (!profile?.company_id) return;
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      const settings = (data?.settings as Record<string, unknown>) || {};
      setStripeConfigured(!!(settings.stripe_secret_key && settings.stripe_publishable_key));
    };
    checkStripe();
  }, [profile?.company_id]);

  const handleStripePayment = async (planType: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setStripeLoading(true);
    setError('');
    setSuccess('');

    try {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const successUrl = window.location.origin + cleanBase + '/subscription?status=stripe_success';
      const cancelUrl = window.location.origin + cleanBase + '/subscription?status=stripe_cancel';

      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-stripe-checkout',
        {
          body: {
            planType,
            successUrl,
            cancelUrl,
          },
        }
      );

      if (invokeError) {
        setError(invokeError.message || 'Erro ao criar checkout Stripe');
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError(data?.detail || 'Erro ao gerar link de pagamento Stripe. Tente novamente.');
      }
    } catch (err) {
      setError('Erro inesperado no Stripe. Tente novamente.');
    } finally {
      setStripeLoading(false);
    }
  };

  const handlePayment = async (planType: string, extraData?: Record<string, unknown>) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingPlan(planType);
    setError('');
    setSuccess('');

    try {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const successUrl = window.location.origin + cleanBase + '/subscription?status=success';
      const failureUrl = window.location.origin + cleanBase + '/subscription?status=failure';
      const pendingUrl = window.location.origin + cleanBase + '/subscription?status=pending';

      const body: Record<string, unknown> = {
        planType,
        successUrl,
        failureUrl,
        pendingUrl,
        ...(extraData || {}),
      };

      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-mercadopago-preference',
        { body }
      );

      if (invokeError) {
        setError(invokeError.message || 'Erro ao criar pagamento');
        return;
      }

      if (data?.initPoint) {
        window.location.href = data.initPoint;
      } else {
        setError('Erro ao gerar link de pagamento. Tente novamente.');
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleBuyCredits = async (pkg: CreditPackage) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingCredit(pkg.id);
    setError('');
    setSuccess('');

    try {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const successUrl = window.location.origin + cleanBase + '/subscription?status=credit_success';
      const failureUrl = window.location.origin + cleanBase + '/subscription?status=failure';
      const pendingUrl = window.location.origin + cleanBase + '/subscription?status=pending';

      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-mercadopago-preference',
        {
          body: {
            planType: 'credits',
            credits: pkg.credits,
            price: pkg.price,
            successUrl,
            failureUrl,
            pendingUrl,
          },
        }
      );

      if (invokeError) {
        setError(invokeError.message || 'Erro ao criar pagamento');
        return;
      }

      if (data?.initPoint) {
        window.location.href = data.initPoint;
      } else {
        setError('Erro ao gerar link de pagamento.');
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoadingCredit(null);
    }
  };

  const handleBuyExcess = async (excess: typeof excessPackages[number]) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingExcess(excess.id);
    setError('');
    setSuccess('');

    try {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const successUrl = window.location.origin + cleanBase + '/subscription?status=excess_success';
      const failureUrl = window.location.origin + cleanBase + '/subscription?status=failure';
      const pendingUrl = window.location.origin + cleanBase + '/subscription?status=pending';

      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-mercadopago-preference',
        {
          body: {
            planType: 'excess',
            extraMessages: excess.amount,
            price: excess.price,
            successUrl,
            failureUrl,
            pendingUrl,
          },
        }
      );

      if (invokeError) {
        setError(invokeError.message || 'Erro ao criar pagamento');
        return;
      }

      if (data?.initPoint) {
        window.location.href = data.initPoint;
      } else {
        setError('Erro ao gerar link de pagamento.');
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoadingExcess(null);
    }
  };

  const handleSaveConnection = async () => {
    if (!user) return;
    setError('');
    setSuccess('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ connection_type: connectionType })
        .eq('id', user.id);

      if (updateError) {
        setError('Erro ao salvar preferência: ' + updateError.message);
      } else {
        setSuccess('Preferência salva com sucesso!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Erro inesperado.');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setSuccess('Pagamento aprovado! Seu plano será atualizado em instantes.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'credit_success') {
      setSuccess('Pagamento aprovado! Seus créditos foram adicionados.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'excess_success') {
      setSuccess('Pagamento aprovado! Mensagens extras adicionadas ao seu limite.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'stripe_success') {
      setSuccess('Pagamento Stripe aprovado! Seu plano será atualizado em instantes.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'stripe_cancel') {
      setError('Pagamento cancelado no Stripe.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'failure') {
      setError('Pagamento não aprovado. Tente novamente ou use outro cartão.');
      window.history.replaceState({}, '', '/subscription');
    } else if (status === 'pending') {
      setSuccess('Pagamento pendente. Assim que for aprovado, será ativado.');
      window.history.replaceState({}, '', '/subscription');
    }
  }, []);

  const currentPlanInfo = plansList.find((p) => p.planKey === currentPlan);

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
          Planos e Assinatura
        </h1>
        <p className="text-sm text-foreground-500 dark:text-foreground-400">
          Gerencie seu plano, créditos e forma de conexão WhatsApp
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
          <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
          <i className="ri-check-line text-emerald-500 text-lg flex-shrink-0" />
          <p className="text-sm text-emerald-700 dark:text-emerald-400">{success}</p>
        </div>
      )}

      {/* Trial Banner */}
      <TrialBanner variant="subscription" />

      {/* Status Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Current Plan */}
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-vip-crown-line text-xl text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-foreground-500">Plano atual</p>
              <p className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 capitalize">
                {currentPlanInfo?.name || 'Starter'}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Limite mensal</span>
              <span className="font-semibold text-foreground-800 dark:text-foreground-200">
                {monthlyLimit.toLocaleString('pt-BR')} msgs
              </span>
            </div>
            <div className="w-full h-2 bg-background-200 dark:bg-background-300 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <p className="text-[10px] text-foreground-400 text-right">
              {sentThisMonth.toLocaleString('pt-BR')} usadas ({usagePercent}%)
            </p>
          </div>
        </div>

        {/* Credits */}
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-coin-line text-xl text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <p className="text-xs text-foreground-500">Créditos de Envio</p>
              <p className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100">
                {currentCredits.toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
          <p className="text-[10px] text-foreground-400">
            {connectionType === 'own'
              ? 'API própria — sem consumo de créditos WZAP'
              : 'Consumidos apenas na infraestrutura WZAP'}
          </p>
        </div>

        {/* Connection Type */}
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Conexão</h3>
          <div className="space-y-2">
            <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
              connectionType === 'wzap'
                ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-500/10'
                : 'border-background-200 hover:border-background-300'
            }`}>
              <input
                type="radio"
                name="connection"
                value="wzap"
                checked={connectionType === 'wzap'}
                onChange={() => setConnectionType('wzap')}
                className="mt-0.5 accent-primary-500"
              />
              <div>
                <p className="text-xs font-medium text-foreground-900 dark:text-foreground-100">WhatsApp WZAP</p>
                <p className="text-[10px] text-foreground-500">Infraestrutura gerenciada</p>
              </div>
            </label>
            <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
              connectionType === 'own'
                ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-500/10'
                : 'border-background-200 hover:border-background-300'
            }`}>
              <input
                type="radio"
                name="connection"
                value="own"
                checked={connectionType === 'own'}
                onChange={() => setConnectionType('own')}
                className="mt-0.5 accent-primary-500"
              />
              <div>
                <p className="text-xs font-medium text-foreground-900 dark:text-foreground-100">Minha API Meta</p>
                <p className="text-[10px] text-foreground-500">Você controla sua API</p>
              </div>
            </label>
          </div>
          <button
            type="button"
            onClick={handleSaveConnection}
            className="w-full mt-3 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          >
            Salvar
          </button>
        </div>
      </div>

      {/* Account Details Card */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-secondary-50 dark:bg-secondary-500/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-settings-line text-xl text-secondary-600 dark:text-secondary-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">Detalhes da Conta</p>
            <p className="text-xs text-foreground-500">Informações sobre sua assinatura e acesso</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300">
            <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">Cliente desde</p>
            <p className="text-sm font-semibold text-foreground-950 dark:text-foreground-100">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('pt-BR') : '—'}
            </p>
          </div>
          {currentPlan === 'trial' && trialEndDate && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/10">
              <p className="text-[10px] text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">Teste expira em</p>
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                {trialEndDate.toLocaleDateString('pt-BR')}
                <span className="text-xs font-normal text-red-500 dark:text-red-400 ml-1">
                  ({trialDaysRemaining !== null && trialDaysRemaining > 0 ? `${trialDaysRemaining} dias` : 'hoje'})
                </span>
              </p>
            </div>
          )}
          {currentPlan !== 'trial' && (
            <div className="p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300">
              <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">Plano ativo desde</p>
              <p className="text-sm font-semibold text-foreground-950 dark:text-foreground-100">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('pt-BR') : '—'}
              </p>
            </div>
          )}
          <div className="p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300">
            <p className="text-[10px] text-foreground-500 uppercase tracking-wider mb-1">Forma de conexão</p>
            <p className="text-sm font-semibold text-foreground-950 dark:text-foreground-100 capitalize">
              {currentConnection === 'wzap' ? 'WhatsApp WZAP' : 'Minha API Meta'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-background-100 dark:bg-background-200 rounded-full p-1">
        {(['planos', 'excedente', 'creditos'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab
                ? 'bg-white dark:bg-background-100 text-foreground-950 dark:text-foreground-100 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {tab === 'planos' && <><i className="ri-vip-crown-line mr-1.5" />Planos</>}
            {tab === 'excedente' && <><i className="ri-add-circle-line mr-1.5" />Excedente</>}
            {tab === 'creditos' && <><i className="ri-coin-line mr-1.5" />Créditos</>}
          </button>
        ))}
      </div>

      {activeTab === 'planos' && (
        <>
          {/* Gateway selector */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-foreground-500">Gateway de pagamento:</span>
            <div className="inline-flex bg-background-100 dark:bg-background-200 rounded-full p-1">
              <button
                type="button"
                onClick={() => setPaymentGateway('mercadopago')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  paymentGateway === 'mercadopago'
                    ? 'bg-white dark:bg-background-100 text-foreground-950 dark:text-foreground-100 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-bank-card-line mr-1" />
                MercadoPago
              </button>
              <button
                type="button"
                onClick={() => setPaymentGateway('stripe')}
                disabled={!stripeConfigured}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  paymentGateway === 'stripe'
                    ? 'bg-white dark:bg-background-100 text-foreground-950 dark:text-foreground-100 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                <i className="ri-visa-line mr-1" />
                Stripe
                {!stripeConfigured && <span className="ml-1 text-[10px]">(não configurado)</span>}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plansList.map((plan) => {
              const isCurrentPlan = plan.planKey === currentPlan;
              const isLoading = loadingPlan === plan.planKey || stripeLoading;

              return (
                <div
                  key={plan.planKey}
                  className={`relative bg-white dark:bg-background-100 rounded-2xl border p-5 flex flex-col ${
                    plan.highlighted
                      ? 'border-primary-500 ring-4 ring-primary-500/10'
                      : isCurrentPlan
                        ? 'border-emerald-300 dark:border-emerald-500/30'
                        : 'border-background-200 dark:border-background-300'
                  }`}
                >
                  {plan.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      {plan.tag}
                    </span>
                  )}
                  {isCurrentPlan && !plan.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      Plano Atual
                    </span>
                  )}

                  <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-2xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100">{plan.price}</span>
                    {plan.period && <span className="text-sm text-foreground-500">{plan.period}</span>}
                  </div>

                  <div className="space-y-1 mb-4 pb-4 border-b border-background-100 dark:border-background-200">
                    <p className="text-xs text-foreground-600 flex items-center gap-1.5 font-semibold">
                      <i className="ri-chat-3-line text-primary-500 w-4 text-center" />
                      {plan.messagesPerMonth}
                    </p>
                    <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <i className="ri-user-line text-primary-500 w-4 text-center" />{plan.users}
                    </p>
                    <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <i className="ri-smartphone-line text-primary-500 w-4 text-center" />{plan.whatsapps}
                    </p>
                    {plan.setupFee > 0 && (
                      <p className="text-xs text-foreground-400 flex items-center gap-1.5">
                        <i className="ri-tools-line w-4 text-center" />
                        Implantação a partir de R$ {plan.setupFee}
                      </p>
                    )}
                  </div>

                  <p className="text-[10px] text-foreground-400 mb-3 italic">
                    <i className="ri-information-line mr-0.5" />{plan.connectionLabel}
                  </p>

                  <ul className="space-y-1.5 mb-6 flex-1">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-foreground-600 dark:text-foreground-400">
                        <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <div className="w-full text-center text-sm font-semibold px-4 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 whitespace-nowrap cursor-default">
                      <i className="ri-check-double-line mr-1" />Plano Atual
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={() => plan.planKey === 'enterprise' ? window.open('https://wa.me/5554996542326?text=Olá!%20Tenho%20interesse%20no%20plano%20Enterprise%20do%20WZap%20PRO.%20Gostaria%20de%20mais%20informações.', '_blank') : paymentGateway === 'stripe' && stripeConfigured ? handleStripePayment(plan.planKey) : handlePayment(plan.planKey)}
                        disabled={isLoading || (paymentGateway === 'stripe' && !stripeConfigured && plan.planKey !== 'enterprise')}
                        className={`w-full text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                          plan.highlighted
                            ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'
                            : 'bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300'
                        } disabled:opacity-50`}
                      >
                        {isLoading ? (
                          <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />Redirecionando...</>
                        ) : (
                          <>
                            {plan.planKey === 'enterprise' ? <i className="ri-whatsapp-line" /> : paymentGateway === 'stripe' ? <i className="ri-visa-line" /> : <i className="ri-bank-card-line" />}
                            {plan.planKey === 'enterprise' ? plan.cta : paymentGateway === 'stripe' ? 'Pagar com Stripe' : plan.cta}
                          </>
                        )}
                      </button>
                      {plan.planKey !== 'enterprise' && stripeConfigured && (
                        <button
                          onClick={() => paymentGateway === 'mercadopago' ? handleStripePayment(plan.planKey) : handlePayment(plan.planKey)}
                          disabled={isLoading}
                          className="w-full text-xs font-medium px-3 py-1.5 rounded-lg text-foreground-500 hover:text-foreground-700 hover:bg-background-100 border border-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                        >
                          {paymentGateway === 'mercadopago' ? (
                            <><i className="ri-visa-line" />ou pagar com Stripe</>
                          ) : (
                            <><i className="ri-bank-card-line" />ou pagar com MercadoPago</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── EXCEDENTE ── */}
      {activeTab === 'excedente' && (
        <div>
          <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-3">
              <i className="ri-lightbulb-flash-line text-amber-600 dark:text-amber-400 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                  Acabou a franquia do seu plano?
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Compre mensagens extras avulsas. Exemplo: plano Professional (50 mil) + 30 mil extras = R$ 397/mês total.
                  {connectionType === 'own' && (
                    <span className="block mt-1 font-medium">
                      Usando API própria Meta: você paga o custo por mensagem diretamente à Meta por categoria (marketing, utilidade, autenticação).
                      O excedente aqui é apenas para infraestrutura WZAP.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {excessPackages.map((pkg) => {
              const isLoading = loadingExcess === pkg.id;
              return (
                <div
                  key={pkg.id}
                  className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5 text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mx-auto mb-3">
                    <i className={`${pkg.icon} text-xl text-accent-600 dark:text-accent-400`} />
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 dark:text-foreground-100 mb-1">
                    {pkg.name}
                  </p>
                  <p className="text-2xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100 mb-2">
                    R$ {pkg.price.toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-foreground-500 mb-4">
                    adicional à sua franquia atual
                  </p>
                  <button
                    onClick={() => handleBuyExcess(pkg)}
                    disabled={isLoading}
                    className="w-full text-sm font-semibold px-4 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-white transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />Redirecionando...</>
                    ) : (
                      <><i className="ri-shopping-cart-line" />Comprar</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Setup Fee Info */}
          <div className="mt-6 bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-3">
              <i className="ri-tools-line mr-1.5 text-primary-500" />
              Taxa de Implantação (setup único)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background-50 dark:bg-background-200 rounded-xl border border-background-200 dark:border-background-300">
                <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 mb-1">Básica — R$ 297</p>
                <ul className="text-xs text-foreground-500 space-y-1">
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Configuração de 1 WhatsApp</li>
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Integração com API</li>
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Treinamento básico (1h)</li>
                </ul>
              </div>
              <div className="p-4 bg-background-50 dark:bg-background-200 rounded-xl border border-primary-200 dark:border-primary-500/20">
                <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 mb-1">Completa — R$ 997</p>
                <ul className="text-xs text-foreground-500 space-y-1">
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Configuração de múltiplos WhatsApps</li>
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Integração + CRM</li>
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Treinamento completo (4h)</li>
                  <li><i className="ri-check-line text-emerald-500 mr-1" />Suporte na migração</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CRÉDITOS ── */}
      {activeTab === 'creditos' && (
        <div>
          <p className="text-sm text-foreground-500 mb-4">
            Compre créditos de envio para usar com a infraestrutura WZAP. Se você usa API própria Meta, não precisa de créditos.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {creditPackages.map((pkg) => {
              const isLoading = loadingCredit === pkg.id;
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-white dark:bg-background-100 rounded-2xl border p-5 text-center ${
                    pkg.highlighted
                      ? 'border-accent-500 ring-2 ring-accent-500/10'
                      : 'border-background-200 dark:border-background-300'
                  }`}
                >
                  {pkg.highlighted && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      Melhor Custo-Benefício
                    </span>
                  )}
                  <div className="w-12 h-12 rounded-full bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-stack-line text-xl text-accent-600 dark:text-accent-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 dark:text-foreground-100 mb-1">{pkg.name}</p>
                  <p className="text-2xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100 mb-2">
                    R$ {pkg.price.toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-foreground-500 mb-4">
                    {pkg.credits.toLocaleString('pt-BR')} envios via WZAP
                  </p>
                  <button
                    onClick={() => handleBuyCredits(pkg)}
                    disabled={isLoading}
                    className={`w-full text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      pkg.highlighted
                        ? 'bg-accent-500 hover:bg-accent-600 text-white'
                        : 'bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 border border-background-200 dark:border-background-300'
                    } flex items-center justify-center gap-2`}
                  >
                    {isLoading ? (
                      <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />Redirecionando...</>
                    ) : (
                      <><i className="ri-shopping-cart-line" />Comprar Créditos</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}